import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { canSendPacks, isSuperAdmin } from "@/lib/permissions";

// Audit V1: admin page for stable shareable demo-pack links.
type DemoPack = {
  id: string;
  slug: string;
  playlistId: string;
  playlistName: string;
  campaignId: string | null;
  welcomeMessage: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  openCount: number;
  createdBy: string;
  createdByUserId?: string;
  createdAt: string;
  publicUrl: string;
  // #2622: backing playlist still contains NDA-tier videos (created before the
  // publish block). Those videos are hidden from viewers; the admin should fix.
  hasNdaTierVideo?: boolean;
};

export function DemoPacksPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  // Mirror of the server rules: revoke = super_admin (any) or the AE who
  // created the link; create = anyone who can send packs.
  const canRevoke = (p: DemoPack) =>
    isSuperAdmin(user?.role) ||
    (user?.role === "ae" && p.createdByUserId === user?.id);
  const [createOpen, setCreateOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["demo-packs"],
    queryFn: () => api.get<DemoPack[]>("/api/admin/demo-packs"),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/demo-packs/${id}/revoke`, {}),
    onSuccess: () => {
      toast.success("Revoked");
      qc.invalidateQueries({ queryKey: ["demo-packs"] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed"),
  });

  function copy(url: string) {
    navigator.clipboard.writeText(url).then(
      () => toast.success("Copied", { id: "copy-link" }),
      () => toast.error("Copy blocked — select + Cmd-C"),
    );
  }

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center justify-between">
        <h1 className="font-head text-2xl font-semibold">Shared Links</h1>
        {canSendPacks(user?.role) && (
          <Button onClick={() => setCreateOpen(true)}>New shared link</Button>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        Stable shareable links. Each opener identifies themselves and gets their
        own per-recipient issuance + viewer experience.
      </p>
      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-admin-rule">
              <th className="p-3 font-medium">Slug</th>
              <th className="p-3 font-medium">Playlist</th>
              <th className="p-3 font-medium">Opens</th>
              <th className="p-3 font-medium">Status</th>
              <th className="p-3 font-medium">Created</th>
              <th className="p-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(data ?? []).map((p) => (
              <tr key={p.id} className="border-b border-admin-rule">
                <td className="p-3 font-medium">
                  <code className="text-xs">{p.slug}</code>
                </td>
                <td className="p-3">
                  {p.playlistName}
                  {p.hasNdaTierVideo && (
                    <Badge
                      variant="danger"
                      className="ml-2"
                      title="NDA-tier videos cannot be included in public demo packs. Remove them from the playlist or make it a private/NDA-gated pack. They are hidden from viewers until fixed."
                    >
                      NDA-tier hidden
                    </Badge>
                  )}
                </td>
                <td className="p-3">{p.openCount}</td>
                <td className="p-3">
                  {p.revokedAt ? (
                    <Badge variant="muted">revoked</Badge>
                  ) : p.expiresAt && new Date(p.expiresAt).getTime() < Date.now() ? (
                    <Badge variant="muted">expired</Badge>
                  ) : (
                    <Badge variant="ok">active</Badge>
                  )}
                </td>
                <td className="p-3 text-xs text-muted-foreground">
                  {new Date(p.createdAt).toLocaleDateString()} · {p.createdBy}
                </td>
                <td className="p-3 text-right space-x-2">
                  <Button size="sm" variant="outline" onClick={() => copy(p.publicUrl)}>
                    Copy link
                  </Button>
                  {!p.revokedAt && canRevoke(p) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => revoke.mutate(p.id)}
                    >
                      Revoke
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {(data ?? []).length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-muted-foreground text-sm">No demo packs yet</td></tr>
            )}
          </tbody>
        </table>
      </Card>
      <CreateDemoPackDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => qc.invalidateQueries({ queryKey: ["demo-packs"] })}
      />
    </div>
  );
}

function CreateDemoPackDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState({
    playlistId: "",
    welcomeMessage: "",
    customSlug: "",
    expiresInDays: 30,
  });
  const { data: playlists } = useQuery({
    queryKey: ["playlists-demo-pack-create"],
    queryFn: () => api.get<{ items: Array<{ id: string; name: string }> }>("/api/admin/playlists"),
    enabled: open,
  });
  const create = useMutation({
    mutationFn: () =>
      api.post<DemoPack>("/api/admin/demo-packs", {
        playlistId: form.playlistId,
        welcomeMessage: form.welcomeMessage || undefined,
        customSlug: form.customSlug || undefined,
        expiresInDays: form.expiresInDays,
      }),
    onSuccess: () => {
      toast.success("Demo pack created");
      onCreated();
      onOpenChange(false);
      setForm({ playlistId: "", welcomeMessage: "", customSlug: "", expiresInDays: 30 });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed"),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New shared link</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Playlist</Label>
              <Select
                value={form.playlistId}
                onChange={(e) => setForm((f) => ({ ...f, playlistId: e.target.value }))}
              >
                <option value="">Choose a playlist</option>
                {(playlists?.items ?? []).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Welcome message (optional)</Label>
              <Textarea
                rows={3}
                value={form.welcomeMessage}
                onChange={(e) => setForm((f) => ({ ...f, welcomeMessage: e.target.value }))}
                placeholder="Shown on the public landing page…"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Custom slug (optional)</Label>
                <Input
                  value={form.customSlug}
                  onChange={(e) => setForm((f) => ({ ...f, customSlug: e.target.value }))}
                  placeholder="auto-generated if blank"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Expires in (days)</Label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={form.expiresInDays}
                  onChange={(e) => setForm((f) => ({ ...f, expiresInDays: Number(e.target.value) }))}
                />
              </div>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => create.mutate()}
            disabled={!form.playlistId || create.isPending}
          >
            {create.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
